import { uiText } from '../i18n/uiText';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Bell, CheckCircle, XCircle, Clock, CalendarDays,
  RefreshCw, MessageSquare, ChevronDown, ChevronUp,
} from 'lucide-react';
import { auditAssignmentsApi, getApiErrorMessage } from '../api';
import { useAuth } from '../store/AuthContext';
import { Link } from 'react-router-dom';
import PageIntro from '../components/ui/PageIntro';
import Badge from '../components/ui/Badge';

function fmt(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function NotificationCard({ item, onRespond }) {
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const respond = async (action) => {
    setBusy(true);
    setError('');
    try {
      await auditAssignmentsApi.respond(item.id, {
        action,
        note: action === 'decline' ? note.trim() || null : null,
      });
      onRespond();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const statusMeta = {
    pending:  { label: uiText("Ожидает вашего ответа"), color: '#b45309', bg: '#fffbeb', icon: <Clock size={14} /> },
    accepted: { label: uiText("Вы подтвердили участие"),  color: '#15803d', bg: '#f0fdf4', icon: <CheckCircle size={14} /> },
    declined: { label: uiText("Вы отказались"),            color: '#b91c1c', bg: '#fef2f2', icon: <XCircle size={14} /> },
  };
  const sm = statusMeta[item.status] || statusMeta.pending;

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: 14,
      overflow: 'hidden',
      background: '#fff',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
              {item.audit_title || uiText("Аудит #{v0}", {v0: (item.audit_id)})}
            </div>
            <div style={{ fontSize: 12, color: 'var(--premium-text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <CalendarDays size={12} />
              {item.audit_scheduled_date ? fmt(item.audit_scheduled_date) : uiText("Дата не указана")}
            </div>
          </div>
          <span style={{
            fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '3px 12px',
            background: sm.bg, color: sm.color,
            display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
          }}>
            {sm.icon}{sm.label}
          </span>
        </div>

        {item.note && (
          <div style={{
            marginTop: 12, padding: '10px 14px',
            background: '#f8fafc', borderRadius: 8, borderLeft: '3px solid #60a5fa',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
              <MessageSquare size={11} />{uiText(" Примечание от назначающего")}</div>
            <div style={{ fontSize: 13, color: '#374151' }}>{item.note}</div>
          </div>
        )}

        {item.response_note && item.status === 'declined' && (
          <div style={{
            marginTop: 10, padding: '8px 12px',
            background: '#fef2f2', borderRadius: 8, borderLeft: '3px solid #f87171',
            fontSize: 13, color: '#7f1d1d', fontStyle: 'italic',
          }}>{uiText("Ваш отказ: «")}{item.response_note}»
          </div>
        )}
      </div>

      {/* Actions (only if pending) */}
      {item.status === 'pending' && (
        <div style={{ padding: '14px 20px', background: '#fafafa' }}>
          {error && (
            <div style={{ marginBottom: 10, fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
              <XCircle size={12} />{error}
            </div>
          )}

          {declining ? (
            <div>
              <textarea
                className="form-control"
                rows={2}
                placeholder={uiText("Укажите причину отказа (необязательно)…")}
                value={note}
                onChange={e => setNote(e.target.value)}
                style={{ fontSize: 13, marginBottom: 10 }}
                autoFocus
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => { setDeclining(false); setNote(''); }}>{uiText("Отмена")}</button>
                <button type="button"
                  className="btn btn-sm"
                  style={{ background: '#dc2626', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: 5 }}
                  disabled={busy}
                  onClick={() => respond('decline')}
                >
                  <XCircle size={13} />{busy ? uiText("Отправка…") : uiText("Подтвердить отказ")}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button"
                className="btn btn-sm"
                style={{ background: '#15803d', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
                disabled={busy}
                onClick={() => respond('accept')}
              >
                <CheckCircle size={14} />{busy ? uiText("Отправка…") : uiText("Принять участие")}
              </button>
              <button type="button"
                className="btn btn-outline btn-sm"
                style={{ color: '#dc2626', borderColor: '#fca5a5', display: 'flex', alignItems: 'center', gap: 5 }}
                disabled={busy}
                onClick={() => setDeclining(true)}
              >
                <XCircle size={13} />{uiText(" Отказаться")}</button>
              <Link to={`/audits/${item.audit_id}`} className="btn btn-ghost btn-sm">{uiText("Детали аудита →")}</Link>
            </div>
          )}
        </div>
      )}

      {/* Completed status footer */}
      {item.status !== 'pending' && (
        <div style={{ padding: '10px 20px', background: '#fafafa', display: 'flex', justifyContent: 'flex-end' }}>
          <Link to={`/audits/${item.audit_id}`} className="btn btn-ghost btn-sm">{uiText("Открыть аудит →")}</Link>
        </div>
      )}
    </div>
  );
}

export default function AuditorAssignmentNotificationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all | pending | accepted | declined

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await auditAssignmentsApi.myNotifications();
      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i => filter === 'all' || i.status === filter);
  const pendingCount = items.filter(i => i.status === 'pending').length;

  return (
    <div className="workspace-page auditor-assignment-notifications-page">
      <PageIntro
        icon={Bell}
        title={uiText("Уведомления об аудитах")}
        description={uiText("Приглашения на участие в аудитах. Подтвердите участие или откажитесь с указанием причины.")}
        aside={(
          <div className="page-intro-actions">
            {pendingCount > 0 ? <Badge variant="warning">{pendingCount}{uiText(" ожидает ответа")}</Badge> : null}
            <button type="button" className="btn btn-outline" onClick={load}><RefreshCw size={15} />{uiText(" Обновить")}</button>
          </div>
        )}
      />

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        {[
          { label: uiText("Всего приглашений"), value: items.length },
          { label: uiText("Ожидают ответа"), value: pendingCount, color: '#b45309' },
          { label: uiText("Подтверждено"), value: items.filter(i => i.status === 'accepted').length, color: '#15803d' },
          { label: uiText("Отказано"), value: items.filter(i => i.status === 'declined').length, color: '#b91c1c' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-value" style={s.color ? { color: s.color } : {}}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {error && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { value: 'all',      label: uiText("Все ({v0})", {v0: (items.length)}) },
          { value: 'pending',  label: uiText("Ожидают ответа ({v0})", {v0: (items.filter(i => i.status === 'pending').length)}) },
          { value: 'accepted', label: uiText("Подтверждены", {}) },
          { value: 'declined', label: uiText("Отказы", {}) },
        ].map(t => (
          <button type="button"
            key={t.value}
            className={`tab-btn${filter === t.value ? ' active' : ''}`}
            onClick={() => setFilter(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <Bell size={36} color="#ddd" />
          <h3>{uiText("Нет уведомлений")}</h3>
          <p>{filter === 'all' ? uiText("Вас ещё не приглашали на аудиты") : uiText("Нет уведомлений в этой категории")}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(item => (
            <NotificationCard key={item.id} item={item} onRespond={load} />
          ))}
        </div>
      )}
    </div>
  );
}
